import React, { useEffect, useState } from 'react';
import './styles.scss';
import { VoicePrefs, currentPrefs, loadSavedPrefs, sendPrefs, setServerDefaults } from './voiceSettings';

// The voice HUD (client: voiceChatService.ts), in two separate pieces:
//  - at the bottom centre, the player's own voice: the mic state and the level while talking. It shows when
//    the mic is open or muted, so push-to-talk with nothing going on leaves the screen clean.
//  - at the right, the range of the voice (whisper, speak, shout) in metres.
//    It shows only when the player has just switched the range, never while talking.
// Both are kept discreet: small, translucent, no glow.
// Nobody else's voice is shown: it would give away who is talking, and the name of a stranger.

type Range = 'whisper' | 'normal' | 'shout';

interface HudState {
  active: boolean;
  mic: 'open' | 'muted' | 'idle';
  mode: 'ptt' | 'ptm';
  range: Range;
  units: number;
  level: number;
  flash: boolean;
}

const RANGES: Array<{ id: Range; label: string }> = [
  { id: 'whisper', label: 'Chuchoter' },
  { id: 'normal', label: 'Parler' },
  { id: 'shout', label: 'Crier' },
];

// 1 game unit is 1.42875 cm
const unitsToMeters = (units: number): number => Math.max(1, Math.round(units * 0.0142875));

const BARS = 11;

const Meter: React.FC<{ level: number }> = ({ level }) => {
  const lit = Math.round(level * BARS);
  return (
    <span className="voice-speak__meter" aria-hidden="true">
      {Array.from({ length: BARS }, (_, i) => (
        <span key={i} className={i < lit ? 'is-on' : ''} style={{ height: `${6 + ((i * 7) % 5) * 2 + i}px` }} />
      ))}
    </span>
  );
};

const MicIcon: React.FC<{ muted: boolean }> = ({ muted }) => (
  <svg viewBox="0 0 24 24" width="22" height="22" aria-hidden="true" className="voice-speak__mic">
    <rect x="9" y="3" width="6" height="11" rx="3" />
    <path d="M5.5 11a6.5 6.5 0 0 0 13 0" />
    <line x1="12" y1="17.5" x2="12" y2="21" />
    {muted ? <line x1="4" y1="4" x2="20" y2="20" className="voice-speak__slash" /> : null}
  </svg>
);

type RingPoint = [number, number] | null;

// The ring on the ground around the player, for the moment after the range is switched: the points come
// from the game already projected on the screen (0..1), a point behind the camera is null and breaks the line
const VoiceRing: React.FC = () => {
  const [points, setPoints] = useState<RingPoint[]>([]);
  const [range, setRange] = useState<Range>('normal');
  const [size, setSize] = useState({ w: window.innerWidth, h: window.innerHeight });

  useEffect(() => {
    const onRing = (e: Event) => {
      const d = (e as CustomEvent).detail as { points: RingPoint[]; range?: Range };
      setPoints(d.points ?? []);
      if (d.range) setRange(d.range);
    };
    const onResize = () => setSize({ w: window.innerWidth, h: window.innerHeight });
    window.addEventListener('skymp5-client:voiceRing', onRing);
    window.addEventListener('resize', onResize);
    return () => {
      window.removeEventListener('skymp5-client:voiceRing', onRing);
      window.removeEventListener('resize', onResize);
    };
  }, []);

  if (points.length === 0) return null;

  // Runs of visible points, the ring being closed: the run that ends at the last point joins the one that
  // starts at the first when both are visible
  const runs: Array<Array<[number, number]>> = [];
  let current: Array<[number, number]> = [];
  points.forEach((p) => {
    if (p) current.push(p);
    else if (current.length) {
      runs.push(current);
      current = [];
    }
  });
  if (current.length) runs.push(current);
  if (runs.length > 1 && points[0] && points[points.length - 1]) {
    const last = runs.pop() as Array<[number, number]>;
    runs[0] = [...last, ...runs[0]];
  } else if (runs.length === 1 && points.every((p) => p)) {
    runs[0] = [...runs[0], runs[0][0]];
  }

  return (
    <svg className={`voice-ring voice-ring--${range}`} width={size.w} height={size.h} viewBox={`0 0 ${size.w} ${size.h}`} aria-hidden="true">
      {runs.map((run, i) => (
        <polyline key={i} points={run.map((p) => `${p[0] * size.w},${p[1] * size.h}`).join(' ')} />
      ))}
    </svg>
  );
};

const VoiceHud: React.FC = () => {
  const [hud, setHud] = useState<HudState | null>(null);

  useEffect(() => {
    const onHud = (e: Event) => setHud((e as CustomEvent).detail as HudState);
    // The voice just connected: the server's values are the sliders' start, what the player chose wins
    const onReady = (e: Event) => {
      const d = (e as CustomEvent).detail as VoicePrefs;
      setServerDefaults(d);
      if (loadSavedPrefs()) sendPrefs(currentPrefs());
    };
    window.addEventListener('skymp5-client:voiceHud', onHud);
    window.addEventListener('skymp5-client:voiceReady', onReady);
    return () => {
      window.removeEventListener('skymp5-client:voiceHud', onHud);
      window.removeEventListener('skymp5-client:voiceReady', onReady);
    };
  }, []);

  if (!hud || !hud.active) return <VoiceRing />;
  const open = hud.mic === 'open';
  const muted = hud.mic === 'muted';
  const showSpeak = open || muted;
  const showRange = hud.flash;
  if (!showSpeak && !showRange) return <VoiceRing />;

  // Push-to-mute leaves the mic open all the time: while nothing is said the piece stays small
  const quiet = open && hud.mode === 'ptm' && hud.level === 0;

  return (
    <>
      <VoiceRing />
      {showSpeak ? (
        <div className={'voice-speak' + (open ? ' is-open' : '') + (muted ? ' is-muted' : '') + (quiet ? ' is-quiet' : '')} role="status" aria-live="off">
          <MicIcon muted={muted} />
          {quiet ? null : (
            <>
              <span className="voice-speak__title">{muted ? 'Micro coupé' : 'Tu parles'}</span>
              {open ? <Meter level={hud.level} /> : null}
            </>
          )}
        </div>
      ) : null}

      {showRange ? (
        <div className="voice-range" role="group" aria-label="Portée de la voix">
          <div className="voice-range__head">
            <span className="voice-range__title">Portée</span>
            <span className="voice-range__meters">{unitsToMeters(hud.units)} m</span>
          </div>
          <div className="voice-range__segments">
            {RANGES.map((r) => (
              <span key={r.id} className={'voice-range__segment' + (hud.range === r.id ? ' is-active' : '')}>
                {r.label}
              </span>
            ))}
          </div>
        </div>
      ) : null}
    </>
  );
};

export default VoiceHud;
